import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { nsLayoutStyles } from '../../../../../ns-styles/index.js';

import NSJobComponent from '../../../job.js';

import Company from '../../../../../models/company.js';
import JobSchema from '../../../../../models/job-schema.js';

import { callLambda } from '../../../../../helpers/lambda.js';
import {
  companiesHouseFilingUrl,
  formatTurnoverAmount,
  formatTurnoverDate,
} from '../../../../../helpers/turnover-format.js';

import '../../../../../ns-elements/ns-job-layout.js';
import '../../../../../ns-elements/ns-detail-list.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/company/turnover/confirm.json';
import { jobSchema } from '../../../job-schema.js';

@customElement(NSCompanyTurnoverConfirm.is)
export default class NSCompanyTurnoverConfirm extends NSJobComponent {
  static get is() {
    return 'ns-company-turnover-confirm';
  }

  @state()
  private __company?: Company;

  @state()
  private __companyName = 'this company';

  @state()
  private __formattedAmount = 'N/A';

  @state()
  private __formattedYearEnding = 'N/A';

  @state()
  private __formattedFiledOn = 'N/A';

  @state()
  private __filingHistoryUrl: string | null = null;

  @state()
  private __disputeDisabled = false;

  static get schema(): JobSchema {
    return jobSchema(definition, {
      outcomes: { complete: { type: 'hidden' }, dispute: { type: 'hidden' } },
    });
  }

  static styles = [nsLayoutStyles, ...super.styles];

  async connectedCallback() {
    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    try {
      await super.connectedCallback();

      if (!this._db) throw new Error('Unable to find db service');

      if (!this._auth) throw new Error('Unable to find auth service');

      this.__company = await this.loadJobCompany();

      this.__populateCompanyDisplayValues();
    } catch (err: unknown) {
      this.showError(err);
    }
  }

  private __populateCompanyDisplayValues() {
    if (!this.__company) return;

    this.__companyName = this.__company.name ?? 'this company';
    this.__formattedAmount = formatTurnoverAmount(this.__company.turnover);
    this.__formattedYearEnding = formatTurnoverDate(this.__company.turnover?.year);
    this.__formattedFiledOn = formatTurnoverDate(this.__company.turnover?.filedOn);
    this.__filingHistoryUrl = companiesHouseFilingUrl(this.__company, this.__company.turnover?.transactionId);

    // A dispute can only ever route back to this card once — after `dispute-review`'s `corrected`
    // outcome, source is set to STAFF_CORRECTED and it's the only way this card gets shown a
    // second time. Don't let the customer dispute a figure our own team has already reviewed.
    this.__disputeDisabled = this.__company.turnover?.source === 'STAFF_CORRECTED';
  }

  async _outcomeCompleteHandler() {
    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    this.clearError();

    try {
      if (!this._auth) throw new Error('Missing required auth service');
      if (!this.__company) throw new Error('Missing required company');

      this.loading('Confirming turnover');

      await callLambda(
        'company/turnover-outcome',
        'POST',
        this._auth.token,
        JSON.stringify({
          jobId: this._job.id,
          companyId: this.__company.id,
          outcome: 'complete',
        }),
      );

      await this.outcome('complete');
      this.loaded();
    } catch (err: unknown) {
      this.loaded();
      this.showError(err);
    }
  }

  async _outcomeDisputeHandler() {
    if (this.__disputeDisabled) return;

    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    this.clearError();

    try {
      if (!this._auth) throw new Error('Missing required auth service');
      if (!this.__company) throw new Error('Missing required company');

      this.loading('Flagging turnover for review');

      await callLambda(
        'company/turnover-outcome',
        'POST',
        this._auth.token,
        JSON.stringify({
          jobId: this._job.id,
          companyId: this.__company.id,
          outcome: 'dispute',
        }),
      );

      await this.outcome('dispute');
      this.loaded();
    } catch (err: unknown) {
      this.loaded();
      this.showError(err);
    }
  }

  render() {
    return html`
      <ns-job-layout
        heading="We found this turnover figure for ${this.__companyName} from Companies House"
        image="/images/jobs/companies-house-verification.svg"
        image-label="Companies House filing illustration"
      >
        <div class="job-panel">
          <ns-detail-list
            .rows=${[
              { label: 'Turnover', value: this.__formattedAmount },
              { label: 'For the year ending', value: this.__formattedYearEnding },
              { label: 'Filed with Companies House on', value: this.__formattedFiledOn },
              ...(this.__filingHistoryUrl
                ? [
                    {
                      label: 'Source filing',
                      value: html`
                        <a href="${this.__filingHistoryUrl}" target="_blank" rel="noopener noreferrer">
                          View this filing on Companies House
                        </a>
                      `,
                    },
                  ]
                : []),
            ]}
          ></ns-detail-list>
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <p>
            Please confirm this is correct. If it doesn't look right, let us know and our team will take a closer look.
          </p>
          ${this.__disputeDisabled
            ? html`
                <p class="note">
                  This figure has already been reviewed by our team once. If you still believe it's incorrect, please
                  contact support.
                </p>
              `
            : nothing}
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    return html`
      <wa-button
        variant="brand"
        appearance="outlined"
        type="button"
        ?hidden=${this.__disputeDisabled}
        @click=${() => this._outcomeDisputeHandler()}
      >
        Reject
      </wa-button>
      <wa-button variant="brand" type="button" @click=${() => this._outcomeCompleteHandler()}>Confirm</wa-button>
    `;
  }
}
